/**
 * Storezone — Platform Layout (Superadmin)
 *
 * Top-navigation layout for the Storezone platform admin interface.
 * Uses the 'superadmin' theme to visually distinguish it from tenant views.
 *
 * Structure:
 *   Topbar: [Platform brand] [Nav links] [User menu]
 *   Content: full-width page area
 */

import { type ReactNode } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { Icons } from '@/lib/icons'
import { NotificationBell } from '@/components/NotificationBell'
import './PlatformLayout.css'

interface PlatformLayoutProps {
  children: ReactNode
}

const NAV_ITEMS = [
  { to: '/platform/tenants',  label: 'Tenants',  Icon: Icons.Tenants  },
  { to: '/platform/plans',    label: 'Plans',     Icon: Icons.Plans    },
  { to: '/platform/modules',  label: 'Modules',   Icon: Icons.Modules  },
  { to: '/platform/users',    label: 'Users',     Icon: Icons.Users    },
  { to: '/platform/reports',  label: 'Reports',   Icon: Icons.Reports  },
  { to: '/platform/settings', label: 'Settings',  Icon: Icons.Settings },
]

export function PlatformLayout({ children }: PlatformLayoutProps) {
  const { profile, signOut } = useAuth()
  const navigate = useNavigate()

  async function handleSignOut(e?: any) {
    if (e) e.preventDefault()
    await signOut()
    navigate('/auth/login', { replace: true })
  }

  return (
    <div className="sz-platform">

      {/* Topbar */}
      <header className="sz-platform__topbar">
        <div className="sz-platform__topbar-inner">

          {/* Brand */}
          <NavLink to="/platform" className="sz-platform__brand">
            <span className="sz-platform__brand-mark">SZ</span>
            <span className="sz-platform__brand-label">
              Storezone <span className="sz-platform__brand-suffix">Platform</span>
            </span>
          </NavLink>

          {/* Navigation */}
          <nav className="sz-platform__nav" aria-label="Platform navigation">
            {NAV_ITEMS.map(({ to, label, Icon }) => (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) =>
                  `sz-platform__nav-link${isActive ? ' sz-platform__nav-link--active' : ''}`
                }
              >
                <Icon size={15} />
                {label}
              </NavLink>
            ))}
          </nav>

          {/* User */}
          <div className="sz-platform__user">
            <NotificationBell />
            <span className="sz-platform__user-email">
              {profile?.full_name ?? profile?.email ?? '—'}
            </span>
            <button
              type="button"
              id="platform-signout"
              className="sz-platform__signout"
              onClick={handleSignOut}
              title="Sign out"
            >
              <Icons.LogOut size={15} />
            </button>
          </div>
        </div>
      </header>

      {/* Page content */}
      <main className="sz-platform__content">
        {children}
      </main>
    </div>
  )
}
